import { Bugfender } from '@bugfender/sdk';
import { Alert } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { updateLanguageHeader } from '@/api/language';
import { updateLanguage } from '@/api/user';

import { Icon } from '@/components/Icon/Icon';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';
import { FullPageCenter } from '@/components/Layout/Layout.styled';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { SupportedLanguages } from '@/i18n/i18n';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  fetchBusinessCategories,
  fetchDirectorOccupations,
  fetchOccupations,
} from '@/redux/configs';
import {
  selectConfigsInitialized,
  selectConfigsState,
} from '@/redux/configs/selectors';
import { fetchSigningDocuments } from '@/redux/documentSigning';
import { selectUIState, setUserSelectedLanguage } from '@/redux/ui';
import { fetchAccountTypes, fetchPEPQuestions } from '@/redux/user-data';

import { Language } from '@/types/i18n';
import { UserStatus } from '@/types/user';

import { negotiateUserLanguage } from '@/utils/languages';
import { ensureTenantTranslations } from '@/utils/tenantTranslationLoader';
import { isSupportedLanguage } from '@/utils/translations';

function UserTenantTranslationsGate({ children }: Readonly<PropsWithChildren>) {
  const user = useAppSelector(selectLoggedInUser);
  const { tenant: appTenant, availableLanguages } =
    useAppSelector(selectConfigsState);
  const { i18n } = useTranslation();
  const [isReady, setIsReady] = useState(true);

  const userTenant = user?.tenant;
  const shouldLoadTranslations = Boolean(
    userTenant &&
      appTenant === userTenant &&
      isSupportedLanguage(i18n.language),
  );

  const language = shouldLoadTranslations
    ? negotiateUserLanguage({
        userLang: user?.language,
        i18nLanguage: i18n.language,
        allowedLanguages: availableLanguages,
      })
    : null;

  useEffect(() => {
    if (!shouldLoadTranslations || !userTenant || !language) {
      setIsReady(true);
      return;
    }

    let cancelled = false;
    setIsReady(false);
    updateLanguageHeader(language);

    const loadTranslations = async () => {
      try {
        await ensureTenantTranslations(userTenant, language, i18n);
      } finally {
        if (!cancelled) {
          setIsReady(true);
        }
      }
    };

    loadTranslations();

    return () => {
      cancelled = true;
    };
  }, [shouldLoadTranslations, userTenant, language, i18n, availableLanguages]);

  if (!shouldLoadTranslations) {
    return <>{children}</>;
  }

  if (!isReady) {
    return (
      <FullPageLoaderOverlay
        bgColor={(theme: Theme) => theme.palette.background.paper}
      />
    );
  }

  return <>{children}</>;
}

export function UserLocaleSetup({ children }: Readonly<PropsWithChildren>) {
  const user = useAppSelector(selectLoggedInUser);
  const { userSelectedLanguage: logInSelectedLanguage } =
    useAppSelector(selectUIState);
  const isConfigsInitialized = useAppSelector(selectConfigsInitialized);
  const { t, language, switchLanguage } = useTranslate();
  const {
    value: isChangingLanguage,
    setTrue: showLanguageChangeConfirm,
    setFalse: hideLanguageChangeConfirm,
  } = useBoolean(false);
  const isLanguageFirstChecked = useRef(false);
  const dispatch = useAppDispatch();

  const refetchAfterLanguageChange = useCallback(() => {
    // These API need language to be set to get the correct data
    dispatch(fetchAccountTypes());
    dispatch(fetchPEPQuestions());
    dispatch(fetchSigningDocuments());
    dispatch(fetchOccupations());
    dispatch(fetchDirectorOccupations());
    dispatch(fetchBusinessCategories());
  }, [dispatch]);

  const {
    mutate: changeUserLanguage,
    isPending,
    error,
  } = useMutationWithUserData({
    mutationFn: updateLanguage,
    onError: e => {
      Bugfender.error(e);
    },
    onFinished: (_, { language }) => {
      switchLanguage(language);
      refetchAfterLanguageChange();
    },
  });

  const onChangeUserLanguage = useCallback(
    (newLang: Language) => {
      isLanguageFirstChecked.current = true;
      updateLanguageHeader(newLang);
      changeUserLanguage({ language: newLang });
      dispatch(setUserSelectedLanguage(undefined));
      hideLanguageChangeConfirm();
    },
    [changeUserLanguage, dispatch, hideLanguageChangeConfirm],
  );

  const onKeepCurrentLanguage = useCallback(() => {
    isLanguageFirstChecked.current = true;
    hideLanguageChangeConfirm();
    dispatch(setUserSelectedLanguage(undefined));
  }, [dispatch, hideLanguageChangeConfirm]);

  const onConfirmChangeLanguage = useCallback(() => {
    if (
      logInSelectedLanguage &&
      Object.values(Language).includes(logInSelectedLanguage)
    ) {
      onChangeUserLanguage(logInSelectedLanguage);
    }
  }, [logInSelectedLanguage, onChangeUserLanguage]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!isLanguageFirstChecked.current) {
      if (!user.language) {
        onChangeUserLanguage(language as Language);
        return;
      }
      const newLang = user.language as Language;
      switchLanguage(newLang);
      updateLanguageHeader(newLang);
      refetchAfterLanguageChange();
      isLanguageFirstChecked.current = true;
    }

    if ([UserStatus.Approved, UserStatus.Rejected].includes(user.status)) {
      return;
    }

    // Handle language change requests
    if (
      logInSelectedLanguage &&
      SupportedLanguages.includes(logInSelectedLanguage) &&
      user.language !== logInSelectedLanguage
    ) {
      showLanguageChangeConfirm();
    } else {
      dispatch(setUserSelectedLanguage(undefined));
    }
  }, [
    user,
    logInSelectedLanguage,
    language,
    onChangeUserLanguage,
    showLanguageChangeConfirm,
    dispatch,
    isLanguageFirstChecked,
    switchLanguage,
    refetchAfterLanguageChange,
  ]);

  if (error && !user?.language) {
    return (
      <FullPageCenter>
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {error?.message || t('error.unexpected')}
        </Alert>
      </FullPageCenter>
    );
  }

  if (isPending || !user?.language) {
    return (
      <FullPageLoaderOverlay
        bgColor={(theme: Theme) => theme.palette.neutral.min}
      />
    );
  }

  if (user && !isConfigsInitialized) {
    return (
      <FullPageLoaderOverlay
        bgColor={(theme: Theme) => theme.palette.neutral.min}
      />
    );
  }

  return (
    <>
      {isChangingLanguage && (
        <ConfirmDrawer
          title={t('modalChangeLanguage.title')}
          description={t('modalChangeLanguage.description')}
          secondaryAction={onConfirmChangeLanguage}
          secondaryText={t('modalChangeLanguage.secondaryText')}
          primaryAction={onKeepCurrentLanguage}
          primaryText={t('modalChangeLanguage.primaryText')}
          onClose={onKeepCurrentLanguage}
        />
      )}
      <UserTenantTranslationsGate>{children}</UserTenantTranslationsGate>
    </>
  );
}
